import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/index.css";
import App from "@/App";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
import AdminDashboard from "@/pages/AdminDashboard";
import VendorDashboard from "@/pages/VendorDashboard";
import VendorApplyPage from "@/pages/VendorApplyPage";
import ThankYouPage from "@/pages/ThankYouPage";
import AuthCallback from "@/pages/AuthCallback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import SignContract from "@/pages/SignContract";
import PayBalance from "@/pages/PayBalance";
import InvoicePage from "@/pages/InvoicePage";

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><div className="text-gold">Loading...</div></div>;
  if (!user) return <Navigate to={`/${requiredRole}/login`} replace />;
  if (user.role !== requiredRole) return <Navigate to={`/${user.role}`} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Check URL fragment for session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public site */}
      <Route path="/" element={<App />} />

      {/* Auth callback */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Admin */}
      <Route path="/admin/login" element={<LoginPage portalType="admin" />} />
      <Route path="/admin" element={
        <ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>
      } />

      {/* Vendor */}
      <Route path="/vendor/login" element={<LoginPage portalType="vendor" />} />
      <Route path="/vendor/apply" element={<VendorApplyPage />} />
      <Route path="/thank-you" element={<ThankYouPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/sign/:proposalId" element={<SignContract />} />
      <Route path="/pay-balance/:proposalId" element={<PayBalance />} />
      <Route path="/invoice/:proposalId" element={<InvoicePage />} />
      <Route path="/vendor" element={
        <ProtectedRoute requiredRole="vendor"><VendorDashboard /></ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>,
);

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => {}));
}
